import moment from 'moment';

import { readArchive, MEDIAS_EXTENSIONS } from '../../../util/archive';
import { escapeRegExp, normalizeEmoji } from '../../../util/text';

import { DATE_FORMAT_OUTPUT, MESSAGES_BLOCK_COUNT, MESSAGE_SYSTEM_SENDER_NAME, MESSAGE_ATTACHMENTS_SEPARATOR } from '../ConversationActions';
import { getLinksInMessage } from '../BookActions';

/**
 * Messages WhatsApp exclus de l'import.
 * On ne filtre plus les mentions système utiles (chiffrement, médias absents, création
 * de groupe, .vcf, etc.) : elles donnent du contexte dans le livre.
 * Les notifications « Reacted … to your message » restent exclues : WhatsApp ne rattache
 * pas la réaction au message cible, contrairement au flux Meta (Messenger / Instagram).
 */
const SYSTEM_MESSAGES = [
    /Reacted .+ to your message/i,
];

const MESSAGE_REGEXES = {
    text: /(\[)?([0-9]{1,4}[\/\.-][0-9]{1,2}[\/\.-][0-9]{2,4})(,?\s?à?\s?)([0-9]{1,2}[:\.][0-9]{1,2}[:\.]?[0-9]{0,2}[:]?)\s?((A|P|M|\.|\s)*)?([\]-])?( )(([^:]*):\s)?(.*)/i,
    position: {
        date: 2,
        hour: 4,
        period: 5,
        name: 10,
        content: 11,
    },
};

// WhatsApp: < pièce jointe : file.jpg >, < attached: file.jpg >, etc.
const WHATSAPP_ATTACHMENT_TAG_REGEX = /<\s*(?:pi[eè]ce\s+jointe|attached|(?:fichier|document|audio|vid[eé]o|image|photo|sticker|gif)(?:\s+joint[eè])?)\s*:\s*([^>]+?)\s*>/gi;
const WHATSAPP_BRACKET_FILE_REGEX = /<\s*([^\s<>]+\.(?:jpg|jpeg|png|webp|bmp|gif|mp4|mp3|opus|aac|m4a|mov))\s*>/gi;

/**
 * Mentions ajoutées par WhatsApp à côté du nom de fichier sur les exports Android
 * ("IMG-20240101-WA0001.jpg (fichier joint)"). Ce ne sont pas des légendes : une fois
 * le nom de fichier extrait, il ne doit rien rester dans le texte du message.
 */
const WHATSAPP_ATTACHMENT_PLACEHOLDERS = [
    'fichier joint',
    'file attached',
    'archivo adjunto',
    'datei angeh[äa]ngt',
    'file allegato',
    'bestand bijgevoegd',
    'bijgevoegd bestand',
    'arquivo anexado',
    'ficheiro anexado',
];

const WHATSAPP_PLACEHOLDER_PATTERN = WHATSAPP_ATTACHMENT_PLACEHOLDERS.join('|');
// Entre parenthèses, la mention est sans ambiguïté : on la retire où qu'elle soit.
const WHATSAPP_PLACEHOLDER_PARENTHESIS_REGEX = new RegExp(`\\(\\s*(?:${WHATSAPP_PLACEHOLDER_PATTERN})\\s*\\)`, 'gi');
// Sans parenthèses, on ne retire que si la mention constitue tout le texte restant,
// pour ne jamais amputer une vraie légende qui contiendrait ces mots.
const WHATSAPP_PLACEHOLDER_ALONE_REGEX = new RegExp(`^(?:${WHATSAPP_PLACEHOLDER_PATTERN})$`, 'i');

/**
 * Suffixe ajouté par WhatsApp aux messages édités dans l'export :
 * "Thx <Ce message a été modifié>". On retire la balise, on garde le texte.
 */
const WHATSAPP_EDITED_MARKERS = [
    'ce message a [eé]t[eé] modifi[eé]',
    'this message was edited',
    'este mensaje (?:fue|ha sido) editado',
    'diese nachricht wurde bearbeitet',
    'questo messaggio [eè] stato modificato',
    'dit bericht is (?:bewerkt|gewijzigd)',
    'esta mensagem foi editada',
    'pesan ini diedit',
];

const WHATSAPP_EDITED_MARKER_REGEX = new RegExp(
    `<\\s*(?:${WHATSAPP_EDITED_MARKERS.join('|')})\\s*>`,
    'gi',
);

const dateSeparators = ['/', '-', '.'];
const timeSeparators = [':'];
const partSeparators = ['', ','];
const timezoneSets = ['', 'A', 'a'];
const secondSets = ['', 'ss'];

const DATE_FORMATS_AVAILABLE = [];
dateSeparators.forEach(dateSeparator => {
    timeSeparators.forEach(timeSeparator => {
        partSeparators.forEach(partSeparator => {
            secondSets.forEach(secondSet => {
                timezoneSets.forEach(timezoneSet => {
                    ['D', 'DD'].forEach(day => {
                        ['M', 'MM'].forEach(month => {
                            ['YY', 'YYYY'].forEach(year => {
                                ['H', 'HH'].forEach(hour => {
                                    ['m', 'mm'].forEach(minute => {
                                        const seconds = `${secondSet ? timeSeparator : ''}${secondSet}`;
                                        const timezone = `${timezoneSet ? ' ' : ''}${timezoneSet}`;
                                        DATE_FORMATS_AVAILABLE.push(
                                            `${day}${dateSeparator}${month}${dateSeparator}${year}${partSeparator} ${hour}${timeSeparator}${minute}${seconds}${timezone}`,
                                            `${month}${dateSeparator}${day}${dateSeparator}${year}${partSeparator} ${hour}${timeSeparator}${minute}${seconds}${timezone}`,
                                            `${year}${dateSeparator}${month}${dateSeparator}${day}${partSeparator} ${hour}${timeSeparator}${minute}${seconds}${timezone}`,
                                        );
                                    });
                                });
                            });
                        });
                    });
                });
            });
        });
    });
});

export function readFile(file, options, onProgress) {
    if(file.type.includes('text')) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                onProgress(100);
                resolve([
                    {
                        path: file.name,
                        originalContent: reader.result,
                        content: transformEntry(reader.result, options || {}),
                    },
                ]);
            };
            reader.readAsText(file, 'UTF-8');
        });
    }
    return readArchive(file, options, onProgress).then(entries => {
        return entries.map(entry => {
            return {
                ...entry,
                originalContent: entry.content,
                content: transformEntry(entry.content, options || {}),
            };
        });
    });
}

export function getFiles(file, imagesPaths = [], onProgress = () => {}) {
    if(file.type.includes('zip') || file.name.includes('zip')) {
        return readArchive(
            file,
            { includeRegexFiles: imagesPaths.map(imagePath => new RegExp(escapeRegExp(imagePath), 'gi')) },
            onProgress,
        );
    }
    return Promise.resolve([]);
}

export function canHandleImages(file) {
    return !['text', 'json'].some(type => file.type.includes(type));
}

export function getProfileName(entries) {
    let participants = [];
    entries.forEach(entry => {
        const conversation = JSON.parse(entry.content);
        participants = participants.concat(conversation.participants);
    });
    return escape(normalizeEmoji(participants[0] || ''));
}

export function getThreads(entries) {
    return [];
}

export function getArchiveContent(entries, from, thread, options = { withImages: false, withMedias: false }, onProgress = percentile => {}) {
    const participants = [];
    let messages = [];
    const messagesBlocks = [];
    let images = [];
    let medias = [];

    entries.forEach(entry => {
        onProgress(1);
        // Re-parser originalContent si disponible (txt brut) pour appliquer les évolutions du parser
        const threadData = JSON.parse(
            entry.originalContent
                ? transformEntry(entry.originalContent, options)
                : entry.content,
        );
        messages = messages.concat(threadData.messages);
        threadData.participants.forEach(participant => {
            if(!participants.includes(participant)) {
                participants.push(participant);
            }
        });
    });

    messages = getFormattedMessages(messages, participants, options);

    const iteration = Math.floor(messages.length / MESSAGES_BLOCK_COUNT);
    const percentile = 100 / (iteration + 1);

    for(let i = 0; i <= iteration; i += 1) {
        const messagesBlock = messages.splice(0, MESSAGES_BLOCK_COUNT);

        onProgress(percentile);

        let csv = '';
        messagesBlock.forEach(message => {
            const { messageCsv, messageImages, messageMedias } = message;
            csv += messageCsv;
            images = images.concat(messageImages);
            medias = medias.concat(messageMedias);
        });
        if(messagesBlock[0]) {
            messagesBlocks.push({
                sort: i,
                filename: `messages_${i + 1}.csv`,
                content: csv,
                messages: messagesBlock,
                start: moment(messagesBlock[0].date).toDate(),
                end: moment((messagesBlock[messagesBlock.length - 1] || messagesBlock[0]).date).toDate(),
            });
        }
    }

    return {
        participants,
        messagesBlocks,
        images,
        medias,
    };
}

/**
 * Exports Android FR en horloge 12 h : "11:49 matin", "1:35 après-midi",
 * "6:11 soir", "12:04 nuit", "12:00 midi", "12:00 minuit" (parfois entre
 * parenthèses). On les convertit en AM/PM pour réutiliser le pipeline
 * existant (détection 12 h, token moment A/a).
 *
 * nuit = après minuit (12/1/2/3 nuit → AM), pas le soir.
 * Remplacement limité à l'en-tête : un "ce matin" dans le texte ne bouge pas.
 */
const WHATSAPP_FRENCH_PERIOD_PATTERN = 'apr[eè]s[\\s-]?midi|minuit|matin|midi|soir|nuit';
const WHATSAPP_PAREN_FRENCH_PERIOD_REGEX = new RegExp(`\\s*\\((${WHATSAPP_FRENCH_PERIOD_PATTERN})\\)\\s*`, 'gi');
const WHATSAPP_HEADER_FRENCH_PERIOD_REGEX = new RegExp(
    `^((?:\\[\\s*)?\\d{1,4}[\\/\\.\\-]\\d{1,2}[\\/\\.\\-]\\d{2,4}(?:,?\\s?à?\\s?)\\d{1,2}[:.]\\d{1,2}(?:[:.]\\d{1,2})?)\\s+(${WHATSAPP_FRENCH_PERIOD_PATTERN})\\b`,
    'i',
);

function frenchPeriodToMeridiem(period) {
    const key = String(period || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\s-]+/g, '');
    if(key === 'midi' || key === 'apresmidi' || key === 'soir') {
        return 'PM';
    }
    if(key === 'matin' || key === 'nuit' || key === 'minuit') {
        return 'AM';
    }
    return null;
}

function normalizeLine(line) {
    WHATSAPP_PAREN_FRENCH_PERIOD_REGEX.lastIndex = 0;
    return String(line || '')
        // Exports iOS : espace insécable étroit (U+202F) / NBSP avant a.m./p.m. ou AM/PM
        .replace(/[\u202F\u00A0]/g, ' ')
        .replace(/[\u200E\u200F\uFEFF\u200B]/g, '')
        .replace(/\r$/, '')
        .replace(WHATSAPP_PAREN_FRENCH_PERIOD_REGEX, (_, period) => {
            const meridiem = frenchPeriodToMeridiem(period);
            return meridiem ? ` ${meridiem} ` : _;
        })
        .replace(WHATSAPP_HEADER_FRENCH_PERIOD_REGEX, (_, prefix, period) => {
            const meridiem = frenchPeriodToMeridiem(period);
            return meridiem ? `${prefix} ${meridiem}` : _;
        });
}

function normalizePeriod(period) {
    const stripped = String(period || '')
        .replace(/[.\s\u202F\u00A0]/g, '')
        .trim();
    return frenchPeriodToMeridiem(stripped) || stripped;
}

/**
 * Certains exports (ex. "06/09/2016 22:42:08: Nom: message") utilisent ": "
 * comme séparateur après l'heure. Le regex capture alors un ":" parasite en
 * fin de groupe horaire ("22:42:08:"), ce qui casse la détection des secondes
 * et le parse strict. On retire tout séparateur de fin ; une heure ne se
 * termine jamais par ":" ou ".", donc c'est sans risque pour les autres formats.
 */
function normalizeHour(hour) {
    return String(hour || '').trim().replace(/[:.]+$/, '');
}

/**
 * Parse strict des dates WhatsApp en locale 'en'.
 * Le locale FR de l'app redéfinit le méridien en PD/MD, ce qui fait échouer
 * le token moment A/a sur AM/PM/a.m./p.m. (Invalid date / jour-mois inversés).
 */
function parseWhatsappMoment(dateStr, format) {
    return moment(dateStr, format, 'en', true);
}

function isMessageHeaderLine(line) {
    return MESSAGE_REGEXES.text.test(normalizeLine(line));
}

function isMediaFilename(filename) {
    const normalized = (filename || '').replace(/^<|>$/g, '').trim().toLowerCase();
    return MEDIAS_EXTENSIONS.some(ext => normalized.endsWith(`.${ext}`));
}

function stripAttachmentPlaceholders(text, hasAttachment) {
    const cleaned = (text || '')
        .replace(WHATSAPP_PLACEHOLDER_PARENTHESIS_REGEX, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return hasAttachment && WHATSAPP_PLACEHOLDER_ALONE_REGEX.test(cleaned) ? '' : cleaned;
}

function stripEditedMarkers(text) {
    return (text || '')
        .replace(WHATSAPP_EDITED_MARKER_REGEX, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function extractContentAndAttachments(content) {
    const attachments = [];
    let text = content || '';

    text = text.replace(WHATSAPP_ATTACHMENT_TAG_REGEX, (_, filename) => {
        const name = (filename || '').trim();
        if(isMediaFilename(name)) {
            attachments.push(name.replace(/^<|>$/g, '').toLowerCase());
        }
        return ' ';
    });

    text = text.replace(WHATSAPP_BRACKET_FILE_REGEX, (_, filename) => {
        attachments.push(filename.toLowerCase());
        return ' ';
    });

    const mediaExtPattern = MEDIAS_EXTENSIONS.join('|');
    const filenameRegex = new RegExp(`([\\w\\-.]+\\.(?:${mediaExtPattern}))`, 'gi');
    text = text.replace(filenameRegex, match => {
        attachments.push(match.toLowerCase());
        return ' ';
    });

    text = stripAttachmentPlaceholders(text, attachments.length > 0);
    text = stripEditedMarkers(text);

    return {
        text,
        attachments: [...new Set(attachments)],
    };
}

function transformEntry(entryContent, options = { format: 'text', isBrut: false, dateFormat: null }) {
    const participants = [];
    const messages = [];

    let entryLines = (entryContent || '').split('\n');
    if(entryLines.length === 1) {
        entryLines = (entryContent || '').split('<br>');
    }

    entryLines = entryLines.map(line => line.replace(/[\u200e\u200f\ufeff\u200b]/g, '').trimEnd());

    const detectedFormat = options.dateFormat || detectFormat(entryLines);

    entryLines
        .filter(line => !isSystemMessage(line))
        .forEach(line => {
            const message = parseLine(normalizeLine(line), { ...options, dateFormat: detectedFormat });

            if(message.isHeaderWithoutContent) {
                return;
            }

            if(message.date && message.sender && (message.text || (message.attachments || []).length)) {
                if(!participants.includes(message.sender)) {
                    participants.push(message.sender);
                }
                messages.push(message);
                return;
            }

            const lastMessage = messages[messages.length - 1];
            if(lastMessage && !isMessageHeaderLine(line)) {
                if(lastMessage.text !== null) {
                    // Message média sans texte : la légende suivante devient le texte,
                    // sans saut de ligne initial qui afficherait une bulle vide.
                    lastMessage.text = lastMessage.text
                        ? `${lastMessage.text}\r\n${line}`
                        : line;
                } else {
                    messages.push({
                        date: lastMessage.date,
                        name: lastMessage.name,
                        message: line,
                        attachments: null,
                    });
                }
            }
        });

    return options.format === 'json'
        ? { messages, participants }
        : JSON.stringify({ participants, messages });
}

function parseLine(line, options = { isBrut: false, dateFormat: '' }) {
    const message = {
        text: '',
        attachments: [],
    };

    const matches = MESSAGE_REGEXES.text.exec(line);
    if(!matches) {
        return message;
    }

    const periodRaw = normalizePeriod(matches[MESSAGE_REGEXES.position.period]);
    const periodUpper = periodRaw.toUpperCase();
    const date = `${matches[MESSAGE_REGEXES.position.date]} ${normalizeHour(matches[MESSAGE_REGEXES.position.hour])}${periodRaw ? ` ${periodRaw}` : ''}`;
    const name = (matches[MESSAGE_REGEXES.position.name] || '').trim();
    const content = (matches[MESSAGE_REGEXES.position.content] || '').trim();

    if(date && !content) {
        return { isHeaderWithoutContent: true };
    }

    // Certains exports placent l'expéditeur dans content sans texte : "[date] Bob:"
    if(date && !name && /^[^:]+:\s*$/.test(content)) {
        return { isHeaderWithoutContent: true };
    }

    if(!date || !content || isSystemMessage(content)) {
        return message;
    }

    message.date = options.isBrut
        ? date
        : getFormattedDate(
            date,
            options?.dateFormat,
            (periodUpper === 'PM' || periodUpper === 'P')
                && !/[Aa]\b/.test(options?.dateFormat || ''),
        );
    message.sender = options.isBrut ? name : (name || MESSAGE_SYSTEM_SENDER_NAME);

    if(options.isBrut) {
        message.text = content;
        return message;
    }

    const { text, attachments } = extractContentAndAttachments(content);
    message.text = text;
    message.attachments = attachments;

    return message;
}

function getMessageContent(message, participants, options = { withImages: false, withMedias: false }) {
    const date = moment(message.date).format(DATE_FORMAT_OUTPUT).split('+').shift();
    const attachments = [...getMessageAttachmentsByType(message, 'link')];

    let messageImages = [];
    let messageMedias = [];

    if(options.withImages) {
        messageImages = getMessageAttachmentsByType(message, 'image');
        attachments.push(...messageImages);
    }
    if(options.withMedias) {
        messageMedias = getMessageAttachmentsByType(message, ['audio', 'video']);
        attachments.push(...messageMedias);
    }

    let messageCsv = `${date};`;
    messageCsv += `${message.sender};`;
    messageCsv += `${participants.length > 2 ? participants[0] : participants.indexOf(message.sender) > 0 ? participants[0] : participants[1]};`;
    messageCsv += `${encodeURIComponent(message.text || '')};`;
    messageCsv += 'true;';
    messageCsv += attachments.length ? `${attachments.join(MESSAGE_ATTACHMENTS_SEPARATOR)};` : '';
    messageCsv += '\n';

    return { messageParticipant: message.sender, messageCsv, messageImages, messageMedias, date };
}

function getFormattedMessages(messages, participants = [], options = {}) {
    return messages
        .sort((a, b) => parseInt(moment(a.date).format('X'), 10) - parseInt(moment(b.date).format('X'), 10))
        .filter(message => {
            if(message.text) {
                return true;
            }

            let shouldAdd = false;
            if((message.attachments || []).length) {
                if(options.withImages) {
                    shouldAdd = shouldAdd || getMessageAttachmentsByType(message, 'image').length;
                }
                if(options.withMedias) {
                    shouldAdd = shouldAdd || getMessageAttachmentsByType(message, ['audio', 'video']).length;
                }
            }
            return shouldAdd;
        })
        .map(message => ({
            ...message,
            ...getMessageContent(message, participants, options),
        }))
        .filter(messageData => messageData.messageCsv);
}

function detectFormat(lines) {
    const SAMPLE_SIZE = 50;

    const allValidLines = lines
        .map(line => normalizeLine(line))
        .filter(line => MESSAGE_REGEXES.text.test(line));
    const sampleLines = [
        ...allValidLines.slice(0, SAMPLE_SIZE),
        ...allValidLines.slice(-SAMPLE_SIZE),
    ];

    if(!sampleLines.length) {
        return null;
    }

    const firstMatches = MESSAGE_REGEXES.text.exec(sampleLines[0]);
    if(!firstMatches) {
        return null;
    }

    const datePart = firstMatches[MESSAGE_REGEXES.position.date];
    const timePart = normalizeHour(firstMatches[MESSAGE_REGEXES.position.hour]);

    const sepCounts = {};
    const timeSepCounts = {};
    sampleLines.forEach(l => {
        const m = MESSAGE_REGEXES.text.exec(l);
        if(m) {
            const sep = (m[MESSAGE_REGEXES.position.date].match(/[\/\-.]/) || ['/'])[0];
            sepCounts[sep] = (sepCounts[sep] || 0) + 1;
            const timeSepMatch = normalizeHour(m[MESSAGE_REGEXES.position.hour]).match(/[:.]/);
            if(timeSepMatch) {
                timeSepCounts[timeSepMatch[0]] = (timeSepCounts[timeSepMatch[0]] || 0) + 1;
            }
        }
    });
    const dateSep = Object.entries(sepCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '/';
    // Locales indonésienne, italienne, allemande : l'heure est écrite « 17.46.49 ».
    const timeSep = Object.entries(timeSepCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || ':';

    const hasSeconds = timePart.split(/[:.]/).length === 3;

    const has12hMarker = allValidLines.some(l => {
        const m = MESSAGE_REGEXES.text.exec(l);
        if(!m) {
            return false;
        }
        const period = normalizePeriod(m[MESSAGE_REGEXES.position.period]).toUpperCase();
        return period === 'AM' || period === 'PM' || period === 'A' || period === 'P';
    });

    const firstDateParts = datePart.split(/[\/\-.]/);
    if(firstDateParts[0]?.length === 4) {
        const timeFormats = buildTimeFormatCandidates(hasSeconds, has12hMarker, timeSep);
        return pickBestDateFormat(
            false,
            dateSep,
            'YYYY',
            timeFormats,
            allValidLines,
            timeFormat => `YYYY${dateSep}M${dateSep}D ${timeFormat}`,
        );
    }

    const yearFormat = firstDateParts[2]?.length === 4 ? 'YYYY' : 'YY';

    let dayFirstCount = 0;
    let monthFirstCount = 0;
    sampleLines.forEach(l => {
        const m = MESSAGE_REGEXES.text.exec(l);
        if(m) {
            const parts = m[MESSAGE_REGEXES.position.date].split(/[\/\-.]/);
            if(parts.length >= 2) {
                if(parseInt(parts[0], 10) > 12) {
                    dayFirstCount++;
                } else if(parseInt(parts[1], 10) > 12) {
                    monthFirstCount++;
                }
            }
        }
    });

    if(dayFirstCount === 0 && monthFirstCount === 0) {
        const filenameDateRegex = /[A-Z]+-(\d{4})-(\d{2})-(\d{2})/i;
        allValidLines.forEach(l => {
            const fnMatch = filenameDateRegex.exec(l);
            if(!fnMatch) {
                return;
            }
            const fnMonth = parseInt(fnMatch[2], 10);
            const m = MESSAGE_REGEXES.text.exec(l);
            if(!m) {
                return;
            }
            const parts = m[MESSAGE_REGEXES.position.date].split(/[\/\-.]/);
            if(parts.length < 2) {
                return;
            }
            if(parseInt(parts[0], 10) === fnMonth) {
                monthFirstCount++;
            } else if(parseInt(parts[1], 10) === fnMonth) {
                dayFirstCount++;
            }
        });
    }

    const isUS = monthFirstCount > dayFirstCount;

    const timeFormats = buildTimeFormatCandidates(hasSeconds, has12hMarker, timeSep);

    return pickBestDateFormat(isUS, dateSep, yearFormat, timeFormats, allValidLines);
}

function buildTimeFormatCandidates(hasSeconds, has12hMarker, timeSep = ':') {
    const seconds = hasSeconds ? `${timeSep}ss` : '';
    if(has12hMarker) {
        // hh d'abord pour les exports iOS (heures zero-paddées : 07, 04),
        // h ensuite pour les exports à heure non paddée (8:00 AM). Le scoring
        // de pickBestDateFormat tranche entre les deux selon les vraies lignes.
        return [
            `hh${timeSep}mm${seconds} A`,
            `hh${timeSep}mm${seconds} a`,
            `h${timeSep}mm${seconds} A`,
            `h${timeSep}mm${seconds} a`,
        ];
    }
    return [`HH${timeSep}mm${seconds}`];
}

function buildScoringDateString(m, candidate) {
    const base = `${m[MESSAGE_REGEXES.position.date]} ${normalizeHour(m[MESSAGE_REGEXES.position.hour])}`;
    if(!/[Aa]/.test(candidate)) {
        return base;
    }
    const period = normalizePeriod(m[MESSAGE_REGEXES.position.period]);
    return period ? `${base} ${period}` : base;
}

function buildDateFormatCandidates(isUS, dateSep, yearFormat, timeFormats, buildIsoDatePart) {
    if(buildIsoDatePart) {
        return timeFormats.map(timeFormat => buildIsoDatePart(timeFormat));
    }

    const candidates = [];
    timeFormats.forEach(timeFormat => {
        ['DD', 'D'].forEach(dayToken => {
            ['MM', 'M'].forEach(monthToken => {
                const dateFormat = isUS
                    ? `${monthToken}${dateSep}${dayToken}${dateSep}${yearFormat}`
                    : `${dayToken}${dateSep}${monthToken}${dateSep}${yearFormat}`;
                candidates.push(`${dateFormat} ${timeFormat}`);
            });
        });
    });
    return candidates;
}

function pickBestDateFormat(isUS, dateSep, yearFormat, timeFormats, lines, buildIsoDatePart) {
    const candidates = buildDateFormatCandidates(isUS, dateSep, yearFormat, timeFormats, buildIsoDatePart);
    const SAMPLE_SIZE = 50;
    const sampleLines = lines.length <= SAMPLE_SIZE * 2
        ? lines
        : [...lines.slice(0, SAMPLE_SIZE), ...lines.slice(-SAMPLE_SIZE)];

    const scoreCandidates = targetLines => {
        let bestFormat = candidates[0];
        let bestScore = -1;

        candidates.forEach(candidate => {
            let score = 0;
            targetLines.forEach(line => {
                const m = MESSAGE_REGEXES.text.exec(line);
                if(!m) {
                    return;
                }
                const dateStr = buildScoringDateString(m, candidate);
                if(parseWhatsappMoment(dateStr, candidate).isValid()) {
                    score += 1;
                }
            });
            if(score > bestScore || (score === bestScore && candidate.length > bestFormat.length)) {
                bestScore = score;
                bestFormat = candidate;
            }
        });

        return { bestFormat, bestScore };
    };

    const sampleResult = scoreCandidates(sampleLines);
    const sampleScores = candidates.map(candidate => {
        let score = 0;
        sampleLines.forEach(line => {
            const m = MESSAGE_REGEXES.text.exec(line);
            if(!m) {
                return;
            }
            const dateStr = buildScoringDateString(m, candidate);
            if(parseWhatsappMoment(dateStr, candidate).isValid()) {
                score += 1;
            }
        });
        return score;
    });
    const maxSampleScore = Math.max(...sampleScores);
    const tiedOnSample = sampleScores.filter(score => score === maxSampleScore).length > 1;

    if(!tiedOnSample || sampleLines.length === lines.length) {
        return sampleResult.bestFormat;
    }

    return scoreCandidates(lines).bestFormat;
}

function buildFormatPaddingVariants(format) {
    if(!format) {
        return [];
    }

    const spaceIdx = format.indexOf(' ');
    if(spaceIdx === -1) {
        return [format];
    }

    const datePart = format.slice(0, spaceIdx);
    const timePart = format.slice(spaceIdx + 1);
    const separatorMatch = datePart.match(/[\/\-.]/);
    if(!separatorMatch) {
        return [format];
    }

    const separator = separatorMatch[0];
    const segments = datePart.split(separator);
    const paddingOptions = {
        D: ['D', 'DD'],
        DD: ['D', 'DD'],
        M: ['M', 'MM'],
        MM: ['M', 'MM'],
    };

    const optionSets = segments.map(segment => paddingOptions[segment] || [segment]);

    const timeVariants = (() => {
        if(/\bhh[:.]/.test(timePart)) {
            return [timePart, timePart.replace(/\bhh([:.])/, 'h$1')];
        }
        if(/\bh[:.]/.test(timePart)) {
            return [timePart, timePart.replace(/\bh([:.])/, 'hh$1')];
        }
        // 24 h : certains exports Android écrivent minuit « 0:16:47 » (H)
        // alors que le reste du fichier est en « 14:40:54 » (HH).
        if(/\bHH[:.]/.test(timePart)) {
            return [timePart, timePart.replace(/\bHH([:.])/, 'H$1')];
        }
        if(/\bH[:.]/.test(timePart)) {
            return [timePart, timePart.replace(/\bH([:.])/, 'HH$1')];
        }
        return [timePart];
    })();

    const combine = (index, current) => {
        if(index >= optionSets.length) {
            return timeVariants.map(time => `${current.join(separator)} ${time}`);
        }
        return optionSets[index].flatMap(token => combine(index + 1, [...current, token]));
    };

    return [...new Set(combine(0, []))];
}

function getFormattedDate(date, preferredFormat = '', addHalfDay = false) {
    const formatsToTry = preferredFormat
        ? buildFormatPaddingVariants(preferredFormat)
        : DATE_FORMATS_AVAILABLE;

    let formattedDate = null;
    formatsToTry.forEach(format => {
        if(!formattedDate) {
            const parsed = parseWhatsappMoment(date, format);
            if(parsed.isValid()) {
                formattedDate = parsed;
            }
        }
    });

    !formattedDate && console.error('Format Date not valid', date, preferredFormat, addHalfDay);
    return moment((formattedDate || moment(date)).add(addHalfDay ? 12 : 0, 'hour').valueOf()).format(DATE_FORMAT_OUTPUT).split('+').shift();
}

function isSystemMessage(line) {
    const text = String(line || '');
    return SYSTEM_MESSAGES.some(systemMessage => {
        if(systemMessage instanceof RegExp) {
            return systemMessage.test(text);
        }
        return text.includes(systemMessage);
    });
}

function getMessageAttachmentsByType(message, types = []) {
    if(types && !Array.isArray(types)) {
        types = [types];
    }

    if(!types || !types.length) {
        types = null;
    }
    const medias = [];

    if(!types || types.includes('link')) {
        medias.push(...(getLinksInMessage(message.text) || []));
    }

    if(!types || types.includes('image')) {
        medias.push(...(message.attachments || []).filter(attachment => attachment && ['jpg', 'jpeg', 'bmp', 'png', 'webp'].some(ext => attachment.toLowerCase().includes(`.${ext}`))).map(attachment => attachment.toLowerCase()));
    }

    if(!types || types.includes('video')) {
        medias.push(...(message.attachments || []).filter(attachment => attachment && ['gif', 'mp4', 'webm', 'mov'].some(ext => attachment.toLowerCase().includes(`.${ext}`))).map(attachment => attachment.toLowerCase()));
    }

    if(!types || types.includes('audio')) {
        medias.push(...(message.attachments || []).filter(attachment => attachment && ['mp3', 'opus'].some(ext => attachment.toLowerCase().includes(`.${ext}`))).map(attachment => attachment.toLowerCase()));
    }

    return medias;
}
